/* <timeline-stepper> — desktop scroll-driven timeline rail + sticky media activation.
   gsap + ScrollTrigger are globals loaded in layout/theme.liquid (registered once in
   bundle.common.js). Mobile card videos are handled with IntersectionObserver only. */
if (!customElements.get('timeline-stepper')) {
  class TimelineStepper extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('.timeline-stepper__step'));
      this.mediaItems = Array.from(this.querySelectorAll('.timeline-stepper__media-item'));
      this.progress = this.querySelector('.timeline-stepper__progress');
      this.stepsWrap = this.querySelector('.timeline-stepper__steps-wrap');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.activeIndex = -1;
      this.triggers = [];
      this.matchMediaContext = null;
      this.fallbackObserver = null;
      this.cardVideoObserver = null;

      this.onBlockSelect = this.onBlockSelect.bind(this);
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.observeCardVideos();

      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;

      this.whenGsapReady().then((hasGsap) => {
        if (!this.isConnected || generation !== this.generation) return;
        if (hasGsap && !this.reducedMotion.matches) {
          this.initScrollTriggers();
        } else {
          this.initObserverFallback();
        }
      });
    }

    disconnectedCallback() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.matchMediaContext) this.matchMediaContext.revert();
      this.triggers.forEach((trigger) => trigger.kill());
      this.triggers = [];
      if (this.fallbackObserver) this.fallbackObserver.disconnect();
      if (this.cardVideoObserver) this.cardVideoObserver.disconnect();
    }

    whenGsapReady() {
      return new Promise((resolve) => {
        let attempts = 0;
        const check = () => {
          if (window.gsap && window.ScrollTrigger) return resolve(true);
          if (attempts >= 50) return resolve(false);
          attempts += 1;
          setTimeout(check, 100);
        };
        check();
      });
    }

    initScrollTriggers() {
      this.matchMediaContext = window.gsap.matchMedia();
      this.matchMediaContext.add('(min-width: 990px)', () => {
        this.layoutProgress();

        if (this.progress && this.steps.length > 1) {
          this.triggers.push(
            window.ScrollTrigger.create({
              trigger: this.stepsWrap,
              start: 'top 60%',
              end: 'bottom 60%',
              scrub: true,
              onUpdate: (self) => window.gsap.set(this.progress, { scaleY: self.progress }),
              onRefreshInit: () => this.layoutProgress(),
            })
          );
        }

        this.steps.forEach((step, index) => {
          this.triggers.push(
            window.ScrollTrigger.create({
              trigger: step,
              start: 'top 60%',
              end: 'bottom 60%',
              onToggle: (self) => {
                if (self.isActive) {
                  this.activate(index);
                  this.playActiveMedia();
                }
              },
            })
          );
        });

        this.triggers.push(
          window.ScrollTrigger.create({
            trigger: this,
            start: 'top bottom',
            end: 'bottom top',
            onToggle: (self) => {
              if (self.isActive) {
                this.playActiveMedia();
              } else {
                this.pauseAllStackVideos();
              }
            },
          })
        );

        if (this.activeIndex < 0) this.activate(0);

        return () => {
          this.triggers.forEach((trigger) => trigger.kill());
          this.triggers = [];
          this.pauseAllStackVideos();
        };
      });
    }

    /* Used when gsap is unavailable or prefers-reduced-motion: active step still
       switches (media swaps without transition via CSS), no scrubbed line. */
    initObserverFallback() {
      if (!this.steps.length || !('IntersectionObserver' in window)) return;
      this.fallbackObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.activate(this.steps.indexOf(entry.target));
            this.playActiveMedia();
          });
        },
        { rootMargin: '-40% 0px -55% 0px' }
      );
      this.steps.forEach((step) => this.fallbackObserver.observe(step));
    }

    observeCardVideos() {
      const videos = Array.from(this.querySelectorAll('.timeline-stepper__card-media video'));
      if (!videos.length || !('IntersectionObserver' in window)) return;
      this.cardVideoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.playVideo(entry.target);
            } else {
              entry.target.pause();
            }
          });
        },
        { threshold: 0.4 }
      );
      videos.forEach((video) => this.cardVideoObserver.observe(video));
    }

    activate(index) {
      if (index < 0 || index === this.activeIndex) return;
      this.activeIndex = index;
      this.steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === index);
        step.classList.toggle('is-passed', i < index);
      });
      this.mediaItems.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        if (i !== index) {
          const video = item.querySelector('video');
          if (video) video.pause();
        }
      });
    }

    playActiveMedia() {
      if (!this.desktopQuery.matches || this.activeIndex < 0) return;
      const item = this.mediaItems[this.activeIndex];
      const video = item && item.querySelector('video');
      if (video) this.playVideo(video);
    }

    pauseAllStackVideos() {
      this.mediaItems.forEach((item) => {
        const video = item.querySelector('video');
        if (video) video.pause();
      });
    }

    playVideo(video) {
      if (this.reducedMotion.matches) return;
      video.muted = true;
      video.playsInline = true;
      const playing = video.play();
      if (playing) playing.catch(() => {});
    }

    layoutProgress() {
      if (!this.progress || !this.stepsWrap || this.steps.length < 2) return;
      const firstDot = this.steps[0].querySelector('.timeline-stepper__dot');
      const lastDot = this.steps[this.steps.length - 1].querySelector('.timeline-stepper__dot');
      if (!firstDot || !lastDot) return;
      const wrapRect = this.stepsWrap.getBoundingClientRect();
      const firstRect = firstDot.getBoundingClientRect();
      const lastRect = lastDot.getBoundingClientRect();
      const top = firstRect.top - wrapRect.top + firstRect.height / 2;
      const height = lastRect.top + lastRect.height / 2 - (firstRect.top + firstRect.height / 2);
      this.progress.style.top = top + 'px';
      this.progress.style.height = Math.max(height, 0) + 'px';
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const step = event.target.closest('.timeline-stepper__step');
      const index = this.steps.indexOf(step);
      if (index < 0) return;
      this.activate(index);
      this.playActiveMedia();
      step.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('timeline-stepper', TimelineStepper);
}
